import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import FadeInSectionDownToUp from "../../components/FadeAnimations/FadeInSectionDownToUp";
import TrainingForm from "../../components/Training/TrainingForm";

const Training = () => {

    const sampleData = [
        {
            title: "Private Pilot License (PPL)",
            description:
                "Start your aviation journey with a PPL and gain the freedom to fly small aircraft confidently under Visual Flight Rules (VFR).",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/PPL.jpg",
            points: [
                "Understand aircraft systems and flight controls",
                "Master takeoff, navigation, and landing procedures",
                "Comprehensive pre-flight and safety training",
                "Obtain eligibility for solo and cross-country flying",
            ],
        },
        {
            title: "Commercial Pilot License (CPL)",
            description:
                "Transform your passion into a profession — this course equips you with the skills and certifications to become a licensed commercial pilot.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/CPL.jpg",
            points: [
                "Train for multi-engine and advanced flight operations",
                "Gain deep understanding of air regulations and procedures",
                "Advanced navigation and instrument flying",
                "Prepare for airline and charter pilot careers",
            ],
        },
        {
            title: "Multi-Engine Rating (MER)",
            description:
                "Enhance your flight expertise by mastering the operation of multi-engine aircraft for greater performance and safety.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/Multi-engine.jpg",
            points: [
                "Experience dual-engine control and performance handling",
                "Understand emergency protocols for engine failures",
                "Advanced aerodynamics and propulsion training",
                "Boost employability with multi-engine certification",
            ],
        },
        {
            title: "Instrument Rating (IR)",
            description:
                "Learn to navigate and fly safely in low-visibility and adverse weather conditions using advanced instruments and precision flying techniques.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/Instrument-rating.jpg",
            points: [
                "Enhance safety through precision flying in low-visibility",
                "Learn to interpret instrument panels and cockpit displays",
                "Training with certified simulators and real IFR routes",
                "Improve flight confidence in all weather conditions",
            ],
        },
        {
            title: "Hire and Fly",
            description:
                "Enjoy the freedom to rent and pilot aircraft from our fleet — perfect for licensed pilots to gain experience and explore the skies independently.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/Hire&Fly.jpg",
            points: [
                "Choose from a fleet of well-maintained aircraft",
                "Flexible flying hours tailored to your schedule",
                "Ideal for building flight experience post-certification",
                "Enjoy flying under professional guidance when required",
            ],
        },
        {
            title: "Ground School for PPL & CPL",
            description:
                "Strengthen your theoretical foundation with comprehensive ground training covering air law, meteorology, navigation, and flight principles.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/Ground-school.jpg",
            points: [
                "Expert-led theoretical instruction sessions",
                "Detailed preparation for written exams",
                "Covers navigation, regulations, and meteorology",
                "Enhances practical flight understanding and safety",
            ],
        },
        {
            title: "English Language Proficiency Competence Check",
            description:
                "Ensure you meet international aviation communication standards through ICAO-aligned language assessment and training.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/English-language.jpg",
            points: [
                "ICAO-compliant testing and certification",
                "Improve aviation-specific communication skills",
                "Essential for international pilot licensing",
                "Focus on clarity, fluency, and comprehension",
            ],
        },
        {
            title: "Radio License for PPL",
            description:
                "Develop essential radio communication skills for safe and efficient coordination with air traffic control and other pilots.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/Radio-licence.jpg",
            points: [
                "Learn proper aviation radio terminology",
                "Understand ATC protocols and phraseology",
                "Gain confidence in real-world communication scenarios",
                "Mandatory for solo and cross-country flight operations",
            ],
        },
    ];

    const [selectedCard, setSelectedCard] = useState(null);

    const cardVariants = {
        hidden: { opacity: 0, y: 60 },
        visible: (index) => ({
            opacity: 1,
            y: 0,
            transition: { delay: index * 0.15, duration: 0.6 },
        }),
    };

    const cards = [
        {
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/PPL-comp.png",
            titleTop: "PPL",
        },
        {
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/CPL-comp.jpg",
            titleTop: "CPL",
        },
        {
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/IR-comp.jpg",
            titleTop: "IROP",
        },
        {
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/ATPL-comp.jpg",
            titleTop: "ATPL",
        },
    ];

    const sections = [
        {
            titleBlue: "Minimum",
            titleRed: "Requirements",
            description:
                "A person may start his/her Pilot training at the age of 15 but should be 17 at the time of their check ride for the Private Pilot’s License. A person can only go solo at the age of 16 years. Before a person can start, they must be able to pass a medical exam to be able to continue their Pilot training and must be able to read, speak and understand English on a level 4 English proficiency.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/Minimum-Requirements.jpg",
        },
        {
            titleBlue: "International",
            titleRed: "Students",
            description:
                "South Africa is a registered International Civil Aviation Organisation (ICAO) state. This ensures that all our training and flight hours are recognised internationally and will allow all international students to convert their licence in their home country after returning. To study in South Africa, a study visa is required. A study visa allows a foreign national to reside in South Africa while attending 4 Aviators. The applicant must be accepted with a letter of acceptance signed by the registrar, show financial means for the full course, and be covered by a South African medical aid.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/International-Students.jpg",
        },
        {
            titleBlue: "Housing And",
            titleRed: "Transport",
            description:
                "Comfortable surroundings help make for a more productive learning process. 4 Aviators students enjoy the life of a residential community surrounded by all the services and conveniences of a big city and safe environment. Each apartment is fully furnished and includes wifi. Student housing is just 20 minutes from the airport and walking distance from a major mall and shopping centres. Transport is available to and from the airport on a daily basis.",
            img: "https://mhcockpit.com/Media/4%20Aviators/Training/Housing-And-Transport.jpg",
        },
    ];

    const [open, setOpen] = useState(false);


    return (
        <div>
            <section
                className="relative w-full h-screen bg-cover bg-center flex items-center justify-center text-center"
                style={{
                    backgroundImage:
                        "url('https://mhcockpit.com/Media/4%20Aviators/Training/Training-hero.jpg')",
                }}
            >
                {/* Overlay for better readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>

                {/* Centered Content */}
                <motion.div
                    className="relative z-10 px-6 sm:px-10 max-w-3xl"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-snug">
                        Courses Designed to Build <br className="hidden sm:block" />
                        World-Class <span className="text-[#FD9F27]">Pilots</span>
                    </h1>

                    <p className="mt-4 text-base sm:text-lg md:text-xl text-white">
                        From beginner to advanced training, our aviation programs are crafted
                        to meet global standards and prepare you for a professional career in
                        the skies.
                    </p>
                </motion.div>
            </section>
            <div className="text-center py-10 space-y-3">
                <FadeInSectionDownToUp>
                    <p className="text-5xl font-semibold">
                        Explore Our Training <span className="text-[#E43038]">Programs</span>
                    </p>
                </FadeInSectionDownToUp>
                <FadeInSectionDownToUp>
                    <p className="text-xl px-96 max-sm:px-5">
                        Browse our range of certified aviation courses - each designed to build the knowledge, skill, and confidence needed to become a professional pilot.
                    </p>
                </FadeInSectionDownToUp>
            </div>
            <div>
                <section className="w-full py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6">

                        {/* Grid Layout */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {sampleData.map((item, index) => (
                                <div className={`relative rounded-2xl shadow-xl overflow-hidden transform transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl cursor-pointer max-sm:translate-y-0 
                                    ${index % 2 === 0 ? "translate-y-6" : "-translate-y-6"
                                    }`}>
                                    <motion.div
                                        key={index}
                                        className="h-[500px]"
                                        variants={cardVariants}
                                        initial="hidden"
                                        whileInView="visible"
                                        custom={index}
                                        viewport={{ once: true, amount: 0.3 }}
                                        onClick={() => setSelectedCard(item)}
                                    >
                                        {/* Background Image */}
                                        <div
                                            className="absolute inset-0 bg-cover bg-center"
                                            style={{ backgroundImage: `url(${item.img})` }}
                                        ></div>

                                        {/* Overlay */}
                                        <div className="absolute inset-0 bg-black/60 transition-all duration-500 hover:bg-black/50 rounded-2xl"></div>

                                        {/* Content */}
                                        <div className="relative z-10 flex flex-col justify-end h-full p-6 text-left text-white">
                                            <h3 className="text-2xl font-semibold mb-3">{item.title}</h3>
                                            <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                                                {item.description}
                                            </p>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Modal */}
                    <AnimatePresence>
                        {selectedCard && (
                            <motion.div
                                className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                            >
                                <motion.div
                                    className="bg-white rounded-2xl overflow-hidden w-full max-w-4xl flex flex-col md:flex-row"
                                    initial={{ scale: 0.9, y: 50 }}
                                    animate={{ scale: 1, y: 0 }}
                                    exit={{ scale: 0.9, opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {/* Left Image */}
                                    <div
                                        className="md:w-1/3 w-full h-48 md:h-auto bg-cover bg-center"
                                        style={{ backgroundImage: `url(${selectedCard.img})` }}
                                    ></div>

                                    {/* Right Content */}
                                    <div className="md:w-2/3 w-full p-6 flex flex-col justify-between">
                                        <div>
                                            <h3 className="text-2xl font-bold text-[#012D6F] mb-3">
                                                {selectedCard.title}
                                            </h3>
                                            <p className="text-gray-700 mb-4">{selectedCard.description}</p>

                                            <ul className="list-disc pl-5 text-gray-600 space-y-2 mb-6">
                                                {selectedCard.points.map((point, idx) => (
                                                    <li key={idx}>{point}</li>
                                                ))}
                                            </ul>
                                        </div>

                                        <div className="flex justify-between items-center mt-4">
                                            <button
                                                onClick={() => setSelectedCard(null)}
                                                className="text-gray-600 hover:text-red-500 font-medium"
                                            >
                                                Close
                                            </button>
                                            <button onClick={() => setOpen(true)} className="bg-[#012D6F] text-white px-5 py-2 rounded-xl hover:bg-[#013b8c] transition-all">
                                                Apply Now
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </section>
            </div>
            <div>
                <section
                    className="relative w-full bg-cover bg-center bg-no-repeat py-20"
                    style={{
                        backgroundImage:
                            "url('https://mhcockpit.com/Media/4%20Aviators/Training/Computer-bg.jpg')",
                    }}
                >
                    {/* Overlay for readability */}
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm"></div>

                    <div className="relative max-w-7xl mx-auto px-6 text-center text-white">
                        {/* Title */}
                        <motion.h2
                            className="text-3xl sm:text-4xl font-bold"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            Computer Based <span className="text-[#FD9F27]">Training</span>
                        </motion.h2>

                        {/* Description */}
                        <motion.p
                            className="mt-4 text-base sm:text-lg md:text-xl max-w-2xl mx-auto"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.6 }}
                            viewport={{ once: true }}
                        >
                            Upgrade from books to interactive, visual-based training. Improve your
                            retention and success rate.
                        </motion.p>

                        {/* Cards Grid */}
                        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                            {cards.map((card, index) => (
                                <motion.div
                                    key={index}
                                    className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-500 cursor-pointer p-4 flex flex-col"
                                    initial={{ opacity: 0, y: 40 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.15, duration: 0.6 }}
                                    viewport={{ once: true }}
                                    onClick={() => setOpen(true)}
                                >
                                    {/* Card Image */}
                                    <div className="w-full h-56 bg-cover bg-center rounded-xl" style={{ backgroundImage: `url(${card.img})` }}></div>

                                    {/* Card Content */}
                                    <div className="flex justify-between items-center mt-5 px-2">
                                        <div className="text-left">
                                            <h3 className="text-xl sm:text-2xl font-semibold leading-tight">
                                                <span className="block text-[#012D6F]">{card.titleTop}</span>
                                                <span className="block text-[#E43038]">Material</span>
                                            </h3>
                                        </div>
                                        <ArrowRight className="text-[#012D6F] w-6 h-6 transition-transform duration-300 group-hover:translate-x-2" />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>
            </div>
            <div>
                <section className="w-full py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-6 space-y-24">
                        {sections.map((item, index) => (
                            <motion.div
                                key={index}
                                className={`flex flex-col lg:flex-row items-center gap-10 ${index % 2 !== 0 ? "lg:flex-row-reverse" : ""
                                    }`}
                                initial={{ opacity: 0, y: 60 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                                viewport={{ once: true, amount: 0.3 }}
                            >
                                {/* Text Section */}
                                <div className="w-full lg:w-1/2">
                                    <h2 className="text-3xl sm:text-4xl font-bold leading-snug mb-4">
                                        <span className="text-[#012D6F]">{item.titleBlue} </span>
                                        <span className="text-[#E43038]">{item.titleRed}</span>
                                    </h2>
                                    <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                                        {item.description}
                                    </p>
                                </div>

                                {/* Image Section */}
                                <div className="w-full lg:w-1/2">
                                    <motion.div
                                        className="rounded-2xl overflow-hidden shadow-xl"
                                        whileHover={{ scale: 1.03 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <img
                                            src={item.img}
                                            alt={item.titleRed}
                                            className="w-full h-72 sm:h-80 md:h-96 object-cover"
                                        />
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </section>
            </div>
            {/* Form Modal */}
            <div>
                <TrainingForm open={open} onClose={() => setOpen(false)} />
            </div>
        </div>
    );
};

export default Training;
